import React, { useRef, useEffect } from 'react';

const BUBBLE_COLORS = [
  'rgba(33,150,243,0.55)', // accent blue, more visible
  'rgba(147,197,253,0.65)', // light blue, more visible
  'rgba(120,180,255,0.48)', // soft blue, more visible
  'rgba(255,255,255,0.42)', // white, more visible
  'rgba(197,225,255,0.48)', // very light blue, more visible
];

function randomBetween(a, b) {
  return a + Math.random() * (b - a);
}

const AnimatedBubblesBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  const bubbles = useRef([]);
  const mouse = useRef({ x: -1000, y: -1000 });
  const BUBBLE_COUNT = 18;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    // Create bubbles with random properties
    bubbles.current = Array.from({ length: BUBBLE_COUNT }, () => {
      const r = randomBetween(30, 90);
      return {
        x: randomBetween(r, width - r),
        y: randomBetween(r, height - r),
        r,
        color: BUBBLE_COLORS[Math.floor(Math.random() * BUBBLE_COLORS.length)],
        dx: randomBetween(-0.7, 0.7) || 0.5,
        dy: randomBetween(-0.7, 0.7) || 0.5,
        opacity: randomBetween(0.45, 0.65),
      };
    });

    function animate() {
      ctx.clearRect(0, 0, width, height);
      for (let bubble of bubbles.current) {
        // Mouse repulsion
        const dist = Math.hypot(bubble.x - mouse.current.x, bubble.y - mouse.current.y);
        if (dist < bubble.r + 80) {
          const angle = Math.atan2(bubble.y - mouse.current.y, bubble.x - mouse.current.x);
          bubble.dx += Math.cos(angle) * 0.05;
          bubble.dy += Math.sin(angle) * 0.05;
        }

        // Draw bubble
        ctx.save();
        ctx.globalAlpha = bubble.opacity;
        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.r, 0, 2 * Math.PI);
        ctx.fillStyle = bubble.color;
        ctx.shadowColor = bubble.color;
        ctx.shadowBlur = 50;
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(33,150,243,0.28)';
        ctx.fill();
        ctx.stroke();
        ctx.restore();

        // Move bubble
        bubble.x += bubble.dx;
        bubble.y += bubble.dy;

        // Bounce off left/right edges
        if (bubble.x - bubble.r < 0) {
          bubble.x = bubble.r + 1;
          bubble.dx = Math.abs(bubble.dx) || 0.5;
        }
        if (bubble.x + bubble.r > width) {
          bubble.x = width - bubble.r - 1;
          bubble.dx = -Math.abs(bubble.dx) || -0.5;
        }
        // Bounce off top/bottom edges
        if (bubble.y - bubble.r < 0) {
          bubble.y = bubble.r + 1;
          bubble.dy = Math.abs(bubble.dy) || 0.5;
        }
        if (bubble.y + bubble.r > height) {
          bubble.y = height - bubble.r - 1;
          bubble.dy = -Math.abs(bubble.dy) || -0.5;
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    }
    animate();

    // Handle resize
    function handleResize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    }
    window.addEventListener('resize', handleResize);

    // Mouse move for repulsion
    function handleMouseMove(e) {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    }
    window.addEventListener('mousemove', handleMouseMove);

    // Mouse leave: move mouse far away
    function handleMouseLeave() {
      mouse.current.x = -1000;
      mouse.current.y = -1000;
    }
    window.addEventListener('mouseout', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(135deg, #e3f0ff 0%, #f8fafc 100%)',
      }}
    />
  );
};

export default AnimatedBubblesBackground; 